import { MediaType } from '../graphql/enums';
import {
  documentExtensions,
  getMediaTypeByExt,
  imageExtensions,
  videoExtensions,
} from './mediaUtils';

export type SniffResult = {
  ext: string;
  mime: string;
  type: MediaType;
};

export type ValidationResult =
  | { ok: true; correctedExt?: string; sniffed: SniffResult }
  | { ok: true; sniffed: null }
  | { ok: false; reason: 'unsupported' | 'unreadable'; sniffed: SniffResult | null };

// HEIC/HEIF and MOV all share the ISOBMFF ftyp container; brand bytes distinguish them.
// All HEIC/HEIF brand variants collapse to 'heic' because the Azure Functions processor
// runs them through heic-convert regardless of subtype.
const heicBrands = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'mif1',
  'msf1',
  'heim',
  'heis',
  'hevm',
  'hevs',
]);
const mp4Brands = new Set(['isom', 'iso2', 'mp41', 'mp42', 'avc1', 'dash', 'M4V ']);
const movBrands = new Set(['qt  ', 'moov']);

// Canonical extension map: callers may pass 'jpg' but the sniffer reports 'jpeg'.
const canonicalExtMap: Record<string, string> = {
  jpg: 'jpeg',
  tif: 'tiff',
  heif: 'heic',
};

const matchBytes = (bytes: Uint8Array, offset: number, expected: number[]): boolean => {
  if (bytes.length < offset + expected.length) return false;
  for (let i = 0; i < expected.length; i++) {
    if (bytes[offset + i] !== expected[i]) return false;
  }
  return true;
};

const readAscii = (bytes: Uint8Array, offset: number, length: number): string => {
  if (bytes.length < offset + length) return '';
  let s = '';
  for (let i = 0; i < length; i++) {
    s += String.fromCharCode(bytes[offset + i]);
  }
  return s;
};

const image = (ext: string, mime: string): SniffResult => ({
  ext,
  mime,
  type: MediaType.Image,
});
const video = (ext: string, mime: string): SniffResult => ({
  ext,
  mime,
  type: MediaType.Video,
});
const document = (ext: string, mime: string): SniffResult => ({
  ext,
  mime,
  type: MediaType.Document,
});

export const sniffMediaTypeFromBytes = (bytes: Uint8Array): SniffResult | null => {
  if (matchBytes(bytes, 0, [0xff, 0xd8, 0xff])) return image('jpeg', 'image/jpeg');
  if (matchBytes(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
    return image('png', 'image/png');
  if (
    matchBytes(bytes, 0, [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
    matchBytes(bytes, 0, [0x47, 0x49, 0x46, 0x38, 0x39, 0x61])
  )
    return image('gif', 'image/gif');
  if (matchBytes(bytes, 0, [0x42, 0x4d])) return image('bmp', 'image/bmp');
  if (readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 4) === 'WEBP')
    return image('webp', 'image/webp');
  if (
    matchBytes(bytes, 0, [0x49, 0x49, 0x2a, 0x00]) ||
    matchBytes(bytes, 0, [0x4d, 0x4d, 0x00, 0x2a])
  )
    return image('tiff', 'image/tiff');
  if (readAscii(bytes, 0, 4) === '8BPS')
    return image('psd', 'image/vnd.adobe.photoshop');
  if (readAscii(bytes, 0, 4) === '%PDF') return document('pdf', 'application/pdf');

  if (readAscii(bytes, 4, 4) === 'ftyp') {
    const brand = readAscii(bytes, 8, 4);
    if (heicBrands.has(brand)) return image('heic', 'image/heic');
    if (mp4Brands.has(brand)) return video('mp4', 'video/mp4');
    if (movBrands.has(brand)) return video('mov', 'video/quicktime');
  }

  return null;
};

const canonical = (ext: string): string => canonicalExtMap[ext] ?? ext;

// Extensions the sniffer can positively identify — if bytes are unreadable
// for these, the file is corrupt or mislabeled and should be rejected rather
// than trusted by extension (unlike SVG/Office formats which have no magic bytes).
const sniffableExtensions = new Set([
  'jpeg', 'jpg',
  'png',
  'gif',
  'bmp',
  'webp',
  'tiff', 'tif',
  'psd',
  'pdf',
  'heic', 'heif',
  'mp4',
  'mov',
]);

const extensionsForMediaType = (type: MediaType): string[] => {
  if (type === MediaType.Image) return imageExtensions;
  if (type === MediaType.Video) return videoExtensions;
  if (type === MediaType.Document) return documentExtensions;
  return [];
};

export const validateMediaFile = (
  bytes: Uint8Array,
  claimedExt: string,
): ValidationResult => {
  const lowerClaimed = claimedExt.toLowerCase();
  const sniffed = sniffMediaTypeFromBytes(bytes);

  if (sniffed) {
    const canonicalClaimed = canonical(lowerClaimed);
    if (sniffed.ext === canonicalClaimed) return { ok: true, sniffed };

    const claimedCategoryExts = extensionsForMediaType(sniffed.type);
    if (claimedCategoryExts.includes(lowerClaimed)) {
      return { ok: true, correctedExt: sniffed.ext, sniffed };
    }
    return { ok: false, reason: 'unsupported', sniffed };
  }

  if (sniffableExtensions.has(lowerClaimed)) {
    return { ok: false, reason: 'unsupported', sniffed: null };
  }

  try {
    getMediaTypeByExt(lowerClaimed);
    return { ok: true, sniffed: null };
  } catch {
    return { ok: false, reason: 'unreadable', sniffed: null };
  }
};
